import { clsx } from 'clsx'
import {
  type ComponentPropsWithRef,
  type ComponentPropsWithoutRef,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
} from 'react'

import { AllyText } from '../ally-text/ally-text'
import { Link } from '../link/link'
import { Loader } from '../loader/loader'
import { Text } from '../text/text'
import { type DistributiveOmit, type ForbidKeys } from '../types'

import styles from './button.module.css'
// Base props that are common to both button and anchor
export interface BaseButtonProps {
  category: 'primary' | 'secondary' | 'tertiary' | 'unstyled'
  variant?: 'mysteria' | 'heart'
  text?: ReactNode | string
  disabled?: boolean
  isCompact?: boolean
  isLoading?: boolean
}

// Helper types for element-specific props
type ButtonHTMLProps = DistributiveOmit<ComponentPropsWithRef<'button'>, 'disabled'>
type AnchorHTMLProps = DistributiveOmit<ComponentPropsWithRef<'a'>, 'type'>

// Get keys that are unique to each element type
type ButtonOnlyKeys = Exclude<keyof ButtonHTMLProps, keyof AnchorHTMLProps>
type AnchorOnlyKeys = Exclude<keyof AnchorHTMLProps, keyof ButtonHTMLProps>

// Clean interfaces with automatic prop exclusions
export interface ButtonAsButton
  extends ForbidKeys<BaseButtonProps & ButtonHTMLProps, AnchorOnlyKeys | 'href'> {
  href?: never
}

export interface ButtonAsLink
  extends ForbidKeys<BaseButtonProps & AnchorHTMLProps, ButtonOnlyKeys> {
  href: string
}

// Union type for polymorphic button
export type ButtonProps = ButtonAsButton | ButtonAsLink

// Type guard to narrow the union type
function hasHref(props: ButtonProps): props is ButtonAsLink {
  return 'href' in props && typeof props.href === 'string'
}

const categoryToClassNameMap: Record<
  `${Exclude<BaseButtonProps['category'], 'unstyled'>}-${NonNullable<BaseButtonProps['variant']>}`,
  string
> = {
  'primary-mysteria': styles.primaryMysteria,
  'secondary-mysteria': styles.secondaryMysteria,
  'tertiary-mysteria': styles.tertiaryMysteria,
  'primary-heart': styles.primaryHeart,
  'secondary-heart': styles.secondaryHeart,
  'tertiary-heart': styles.tertiaryHeart,
}

function RightArrowIcon(props: ComponentPropsWithoutRef<'svg'>): ReactElement {
  const { className, ...rest } = props
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...rest}
    >
      <circle cx="9" cy="9" r="9" fill="currentColor" />
      <g className={styles.arrow}>
        <path
          d="M10.125 6.54541L12.5795 8.99996L10.125 11.4545"
          stroke="currentColor"
          strokeWidth="1.22728"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4.60352 9L12.5808 9"
          stroke="currentColor"
          strokeWidth="1.22728"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  )
}

export function Button(props: ButtonProps): ReactElement {
  const {
    category,
    text,
    children,
    className,
    variant = 'mysteria',
    disabled,
    isCompact = false,
    isLoading = false,
  } = props

  const isDisabled = disabled || isLoading

  // Shared content for both button and link variants
  const content = (
    <>
      <span className={clsx(styles.buttonContent, isLoading && styles.buttonContentLoading)}>
        {category === 'tertiary' && <RightArrowIcon className={styles.icon} />}
        {text ? (
          <Text as="span" className={styles.text}>
            {text}
          </Text>
        ) : (
          children
        )}
      </span>
      {isLoading && <Loader size="small" className={styles.buttonLoader} />}
    </>
  )

  // Shared className for both variants
  // if category is unstyled, we don't want to apply any styles to the button
  const buttonClassName =
    category === 'unstyled'
      ? clsx(styles.unstyled, className)
      : clsx(
          styles.button,
          categoryToClassNameMap[`${category}-${variant}`],
          isCompact && styles.compact,
          disabled && styles.disabled,
          className,
        )

  // Use type guard to determine rendering path - no type casting needed!
  if (hasHref(props)) {
    // TypeScript knows this is ButtonAsLink
    const {
      href,
      category: _category,
      text: _text,
      children: _children,
      className: _className,
      variant: _variant,
      disabled: _disabled,
      isCompact: _isCompact,
      isLoading: _isLoading,
      ...linkProps
    } = props

    // Handle disabled state for links
    const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = e => {
      if (disabled) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      // Call original onClick if provided
      linkProps.onClick?.(e)
    }

    return (
      <>
        <Link
          href={href}
          className={buttonClassName}
          onClick={handleLinkClick}
          aria-disabled={isDisabled}
          inert={isDisabled}
          tabIndex={isDisabled ? -1 : undefined}
          data-loading={isLoading ? true : undefined}
          {...linkProps}
        >
          {content}
        </Link>
        {isLoading && <AllyText text="Please wait..." aria-live="polite" />}
      </>
    )
  }

  // TypeScript knows this is ButtonAsButton - no type casting needed!
  const {
    category: _category,
    text: _text,
    children: _children,
    className: _className,
    variant: _variant,
    disabled: _disabled,
    isCompact: _isCompact,
    isLoading: _isLoading,
    ...buttonProps
  } = props

  return (
    <>
      <button
        className={buttonClassName}
        disabled={isDisabled}
        data-loading={isLoading ? true : undefined}
        {...buttonProps}
      >
        {content}
      </button>
      {isLoading && <AllyText text="Please wait..." aria-live="polite" />}
    </>
  )
}
